import {
  CustomLayoutDrawer,
  CustomLayoutDrawerContent,
  DefaultCustomLayoutDrawerProps,
} from 'module/ui/components/atoms/drawer';
import { Loader } from '@rocketlane/ui';
import { useEmailMessagesQuery } from 'queries/email-messages';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { ContentReadyOverlay, HiddenUntilReady } from './content-ready-overlay';
import { EmailSummaryCard, useEmailSummary } from './email-summary-card';
import { EmailThreadNotFoundState } from './email-thread-not-found';
import { EmailTranscript } from './email-transcript';

export type EmailThreadDrawerState = {
  emailThreadId: number;
  subject?: string;
} | null;

type DrawerProps = {
  drawerState: EmailThreadDrawerState;
  onClose: () => void;
};

// No horizontal padding of its own — it relies on EmailTranscript's Scroll
// padding (overridden below to --spacing-06) so the summary chip lines up
// exactly with the message content below it.
const SummaryHeaderPadding = styled.div`
  padding-top: var(--spacing-05);
`;

const EmailThreadDrawerContent = ({
  emailThreadId,
}: {
  emailThreadId: number;
}) => {
  const { data: messages, isLoading: isMessagesLoading } =
    useEmailMessagesQuery(emailThreadId);
  const { summaryPoints, isSummaryLoading } = useEmailSummary(emailThreadId);
  const [isExpandedContentReady, setIsExpandedContentReady] = useState(false);

  useEffect(() => {
    setIsExpandedContentReady(false);
  }, [emailThreadId]);

  // An initial fetch failure already leaves `messages` empty/undefined, so
  // the length check alone covers a stale/deleted thread. Checking `isError`
  // on its own would also fire on a background refetch failure *after* a
  // successful load, hiding an already-visible thread behind the not-found
  // state — so it's deliberately not part of this check.
  const isThreadNotFound = !isMessagesLoading && (messages?.length ?? 0) === 0;
  const isReady = isExpandedContentReady && !isSummaryLoading;

  if (isMessagesLoading) {
    return (
      <div className="flex justify-center items-center h-full">
        <Loader />
      </div>
    );
  }

  if (isThreadNotFound) {
    return <EmailThreadNotFoundState />;
  }

  // The transcript mounts immediately (hidden) so its iframes can start
  // loading in the background; the loader only lifts once every visible
  // message's content — and the summary — have both settled, so everything
  // appears together instead of popping in piecemeal.
  return (
    <div className="relative h-full">
      {!isReady && (
        <ContentReadyOverlay>
          <Loader />
        </ContentReadyOverlay>
      )}
      <HiddenUntilReady $hidden={!isReady}>
        <EmailTranscript
          sourceId={emailThreadId}
          onExpandedContentReady={() => setIsExpandedContentReady(true)}
          horizontalPadding="var(--spacing-06)"
          header={
            summaryPoints.length > 0 ? (
              <SummaryHeaderPadding>
                <EmailSummaryCard
                  key={emailThreadId}
                  data-cy="drawer-summary-card"
                  summaryPoints={summaryPoints}
                />
              </SummaryHeaderPadding>
            ) : undefined
          }
        />
      </HiddenUntilReady>
    </div>
  );
};

export const EmailThreadDrawer = ({ drawerState, onClose }: DrawerProps) => {
  return (
    <CustomLayoutDrawer
      width="1029px"
      open={!!drawerState}
      onClose={onClose}
      {...DefaultCustomLayoutDrawerProps}
    >
      <CustomLayoutDrawerContent>
        <CustomLayoutDrawerContent.Title
          title={
            <div className="text-ellipsis overflow-hidden whitespace-nowrap max-w-[calc(100%-40px)]">
              {drawerState?.subject || (
                <FormattedMessage
                  id="account.email_pages.email_thread_drawer.title"
                  defaultMessage="Email thread"
                />
              )}
            </div>
          }
        />
        <CustomLayoutDrawerContent.Content className="!p-0">
          {drawerState && (
            <EmailThreadDrawerContent
              emailThreadId={drawerState.emailThreadId}
            />
          )}
        </CustomLayoutDrawerContent.Content>
      </CustomLayoutDrawerContent>
    </CustomLayoutDrawer>
  );
};
